/**
 * AuthContext.jsx
 * Global authentication state and helpers for CISO Advisor.
 *
 * Provides:
 *   - currentUser: { id, email, full_name, role, customer_id, permissions, role_meta }
 *   - isAuthenticated: boolean
 *   - login(email, password) → Promise
 *   - logout()
 *   - can(permission) → boolean
 *   - hasRole(role) → boolean
 *
 * Usage:
 *   // Wrap your app
 *   <AuthProvider>
 *     <App />
 *   </AuthProvider>
 *
 *   // In any component
 *   const { currentUser, can, logout } = useAuth();
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const ACCESS_TOKEN_KEY  = 'ciso_access_token';
const REFRESH_TOKEN_KEY = 'ciso_refresh_token';
const USER_KEY          = 'ciso_user';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(() => {
    try {
      const stored = localStorage.getItem(USER_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(true);

  // ── Token helpers ──────────────────────────────────────────────────────

  const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);
  const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN_KEY);

  const saveTokens = (accessToken, refreshToken, user) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    setCurrentUser(user);
  };

  const clearTokens = () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setCurrentUser(null);
  };

  // ── Authenticated fetch ────────────────────────────────────────────────

  const authFetch = useCallback(async (url, options = {}) => {
    const token = getAccessToken();
    const response = await fetch(`${API_BASE}${url}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });

    // Try token refresh on 401
    if (response.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        const newToken = getAccessToken();
        return fetch(`${API_BASE}${url}`, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${newToken}`,
            ...options.headers,
          },
        });
      } else {
        clearTokens();
        window.location.href = '/login';
      }
    }

    return response;
  }, []); // eslint-disable-line

  const tryRefresh = async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return false;
      const data = await res.json();
      localStorage.setItem(ACCESS_TOKEN_KEY, data.access_token);
      return true;
    } catch {
      return false;
    }
  };

  // ── Auth actions ───────────────────────────────────────────────────────

  const login = async (email, password) => {
    const res = await fetch(`${API_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || 'Login failed');
    }

    const data = await res.json();
    saveTokens(data.access_token, data.refresh_token, data.user);
    return data.user;
  };

  const logout = () => {
    clearTokens();
    window.location.href = '/login';
  };

  // ── Validate token on mount ────────────────────────────────────────────

  useEffect(() => {
    const validate = async () => {
      const token = getAccessToken();
      if (!token) { setLoading(false); return; }

      try {
        const res = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const user = await res.json();
          localStorage.setItem(USER_KEY, JSON.stringify(user));
          setCurrentUser(user);
        } else {
          // Try refresh before giving up
          const refreshed = await tryRefresh();
          if (!refreshed) clearTokens();
        }
      } catch {
        clearTokens();
      } finally {
        setLoading(false);
      }
    };
    validate();
  }, []); // eslint-disable-line

  // ── Permission helpers ─────────────────────────────────────────────────

  const can = (permission) => {
    if (!currentUser) return false;
    return currentUser.permissions?.includes(permission) ?? false;
  };

  const hasRole = (role) => {
    if (!currentUser) return false;
    return currentUser.role === role;
  };

  const isSuperAdmin    = () => hasRole('super_admin');
  const isCustomerAdmin = () => hasRole('customer_admin') || hasRole('super_admin');
  const isReadOnly      = () => hasRole('read_only');

  // ── Context value ──────────────────────────────────────────────────────

  const value = {
    currentUser,
    isAuthenticated: !!currentUser,
    loading,
    login,
    logout,
    can,
    hasRole,
    isSuperAdmin,
    isCustomerAdmin,
    isReadOnly,
    authFetch,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>');
  return context;
}

export default AuthContext;
